/**
 * DesignCanvas.jsx — zoomable + pannable + annotatable SVG viewer for
 * the Design Studio. Wraps `react-zoom-pan-pinch` and layers an SVG
 * annotation canvas on top of the drawing.
 *
 * Annotation model (kept portable so refine can pass it to the backend):
 *   { id, kind: 'rect'|'arrow'|'text', x, y, w, h, x1, y1, x2, y2,
 *     label, text, colour }
 * Rects use x/y/w/h. Arrows use x1/y1/x2/y2. Text uses x/y + text.
 * Coordinates are in the SVG's viewBox space so they're resolution
 * independent and can be replayed on any zoom level.
 *
 * The consumer passes:
 *   - `svg` (raw SVG string, already run through fitSvgToContainer)
 *   - `raster` (data URL or absolute URL)
 *   - `tab` ('svg' | 'raster')
 *   - `annotations`, `setAnnotations`
 *   - `tool` ('pan' | 'rect' | 'arrow' | 'text')
 *   - `streaming` (bool — disables markup tools while a generation is live)
 */
import { useRef, useState, useCallback, useEffect } from 'react';
import { TransformWrapper, TransformComponent, useControls } from 'react-zoom-pan-pinch';
import { ZoomIn, ZoomOut, RotateCcw, MousePointer2, Square, ArrowUpRight, Type, Trash2 } from 'lucide-react';

// Palette — annotation strokes must stand out against a white drawing.
const ANN_STROKE = '#F26B1F';
const ANN_FILL = 'rgba(242,107,31,0.15)';

const uid = () => Math.random().toString(36).slice(2, 10);

// Read the drawing's intrinsic viewBox so annotations can be authored
// in the same coordinate system as the SVG itself. Falls back to
// (0, 0, 1200, 800) for legacy drawings that omit a viewBox.
const parseViewBox = (svg) => {
  if (!svg || typeof svg !== 'string') return [0, 0, 1200, 800];
  const m = svg.match(/viewBox\s*=\s*["']([\-0-9.\s]+)["']/i);
  if (!m) return [0, 0, 1200, 800];
  const p = m[1].trim().split(/\s+/).map(Number);
  return p.length === 4 && p.every((n) => Number.isFinite(n)) ? p : [0, 0, 1200, 800];
};

const ToolbarControls = ({ tool, setTool, onClear, streaming }) => {
  const { zoomIn, zoomOut, resetTransform } = useControls();
  return (
    <div className="absolute top-2 left-2 z-10 flex items-center gap-1 rounded-lg p-1 shadow-md"
      style={{ background: 'var(--layer-0)', border: '1px solid var(--hairline)' }}>
      <button onClick={() => zoomIn()} title="Zoom in" className="p-1.5 rounded hover:bg-nexus-bg-tertiary text-nexus-text-primary" data-testid="ds-zoom-in"><ZoomIn size={13} /></button>
      <button onClick={() => zoomOut()} title="Zoom out" className="p-1.5 rounded hover:bg-nexus-bg-tertiary text-nexus-text-primary" data-testid="ds-zoom-out"><ZoomOut size={13} /></button>
      <button onClick={() => resetTransform()} title="Reset view" className="p-1.5 rounded hover:bg-nexus-bg-tertiary text-nexus-text-primary" data-testid="ds-zoom-reset"><RotateCcw size={13} /></button>
      <div className="w-px h-4" style={{ background: 'var(--hairline)' }} />
      {[
        { id: 'pan', Icon: MousePointer2, title: 'Pan (drag to move, wheel to zoom)' },
        { id: 'rect', Icon: Square, title: 'Rectangle markup' },
        { id: 'arrow', Icon: ArrowUpRight, title: 'Arrow markup' },
        { id: 'text', Icon: Type, title: 'Text note' },
      ].map(({ id, Icon, title }) => (
        <button key={id} onClick={() => setTool(id)} title={title} disabled={streaming}
          className="p-1.5 rounded transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            background: tool === id ? '#F26B1F' : 'transparent',
            color: tool === id ? '#fff' : 'var(--text-primary)',
          }}
          data-testid={`ds-tool-${id}`}>
          <Icon size={13} />
        </button>
      ))}
      <div className="w-px h-4" style={{ background: 'var(--hairline)' }} />
      <button onClick={onClear} title="Clear all annotations"
        className="p-1.5 rounded hover:bg-nexus-bg-tertiary text-nexus-text-primary"
        data-testid="ds-annotations-clear">
        <Trash2 size={13} />
      </button>
    </div>
  );
};

export const DesignCanvas = ({
  svg,                // fitted SVG string (may be empty)
  raster,             // data URL or absolute URL for the raster tab
  tab,                // 'svg' | 'raster'
  streaming,
  streamBufLen = 0,
  annotations,
  setAnnotations,
  tool,
  setTool,
  emptyLabel,
}) => {
  const overlayRef = useRef(null);
  const [dragStart, setDragStart] = useState(null);
  const [preview, setPreview] = useState(null);   // shape being drawn right now
  const [textPrompt, setTextPrompt] = useState(null);   // { x, y, id }
  const viewBox = parseViewBox(svg);
  const [vx, vy, vw, vh] = viewBox;

  // Convert a pointer event into viewBox coordinates. The SVG overlay
  // uses the same viewBox as the drawing, so we can trust getBBox math.
  const evToVB = useCallback((e) => {
    const svgEl = overlayRef.current;
    if (!svgEl) return null;
    const pt = svgEl.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const ctm = svgEl.getScreenCTM();
    if (!ctm) return null;
    const p = pt.matrixTransform(ctm.inverse());
    return { x: p.x, y: p.y };
  }, []);

  const handlePointerDown = (e) => {
    if (streaming || tool === 'pan') return;
    e.stopPropagation(); e.preventDefault();
    const p = evToVB(e); if (!p) return;
    if (tool === 'text') {
      setTextPrompt({ x: p.x, y: p.y, id: uid() });
      return;
    }
    setDragStart(p);
    setPreview({ id: uid(), kind: tool, ...(tool === 'rect' ? { x: p.x, y: p.y, w: 0, h: 0 } : { x1: p.x, y1: p.y, x2: p.x, y2: p.y }) });
  };
  const handlePointerMove = (e) => {
    if (!dragStart || !preview) return;
    const p = evToVB(e); if (!p) return;
    if (preview.kind === 'rect') {
      setPreview({ ...preview,
        x: Math.min(dragStart.x, p.x), y: Math.min(dragStart.y, p.y),
        w: Math.abs(p.x - dragStart.x), h: Math.abs(p.y - dragStart.y),
      });
    } else if (preview.kind === 'arrow') {
      setPreview({ ...preview, x2: p.x, y2: p.y });
    }
  };
  const handlePointerUp = () => {
    if (!preview) return;
    // Ignore trivially small markups (accidental clicks)
    const isRealRect = preview.kind === 'rect' && preview.w > 6 && preview.h > 6;
    const isRealArrow = preview.kind === 'arrow' && Math.hypot(preview.x2 - preview.x1, preview.y2 - preview.y1) > 8;
    if (isRealRect || isRealArrow) {
      setAnnotations([...(annotations || []), preview]);
    }
    setDragStart(null); setPreview(null);
  };

  // Global mouseup guard so drags that end outside the canvas still commit.
  useEffect(() => {
    const onUp = () => handlePointerUp();
    window.addEventListener('pointerup', onUp);
    return () => window.removeEventListener('pointerup', onUp);
  }, [preview, dragStart]);

  const removeAnnotation = (id) => setAnnotations((annotations || []).filter((a) => a.id !== id));

  const canDraw = !streaming && tool !== 'pan';
  const showCanvasContent = (tab === 'svg' && svg) || (tab === 'raster' && raster);

  return (
    <div className="flex-1 rounded-lg overflow-hidden relative"
      style={{ background: '#FFFFFF', border: '1px solid var(--hairline)', minHeight: 320 }}
      data-testid="ds-canvas">
      {!showCanvasContent ? (
        // Empty / interrupted / pending state — rendered OUTSIDE
        // TransformWrapper because react-zoom-pan-pinch's
        // TransformComponent drops its children when the wrapped
        // content has a 0×0 bounding box, which swallowed the fallback
        // <p> in past builds. Keep this as a plain absolute overlay so
        // the message is ALWAYS visible when there's nothing to draw.
        <div
          className="absolute inset-0 flex items-center justify-center px-6 text-center"
          data-testid="ds-canvas-empty">
          <p className="text-nexus-text-tertiary text-sm max-w-md">
            {emptyLabel || 'Waiting for first bytes…'}
          </p>
        </div>
      ) : (
      <TransformWrapper
        minScale={0.25} maxScale={8} initialScale={1}
        wheel={{ step: 0.15 }} doubleClick={{ disabled: true }}
        panning={{ disabled: canDraw }}   // don't fight the markup gesture
      >
        {() => (
          <>
            <ToolbarControls
              tool={tool} setTool={setTool} streaming={streaming}
              onClear={() => setAnnotations([])}
            />
            <TransformComponent
              wrapperStyle={{ width: '100%', height: '100%' }}
              contentStyle={{ width: '100%', height: '100%' }}>
              <div className="relative w-full h-full flex items-center justify-center">
                {tab === 'svg' ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: svg }}
                    className="w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg]:max-w-full [&>svg]:max-h-full"
                  />
                ) : (
                  <img src={raster} alt="Generated drawing" className="max-w-full max-h-full object-contain" />
                )}
                {/* Annotation overlay — matches the SVG's viewBox so
                    markups line up with drawing elements. */}
                <svg
                  ref={overlayRef}
                  viewBox={`${vx} ${vy} ${vw} ${vh}`}
                  preserveAspectRatio="xMidYMid meet"
                  className="absolute inset-0 w-full h-full"
                  style={{ pointerEvents: canDraw ? 'auto' : 'none', cursor: canDraw ? 'crosshair' : 'default' }}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  data-testid="ds-annotation-overlay">
                  {(annotations || []).map((a) => (
                    <AnnotationShape key={a.id} a={a} onRemove={removeAnnotation} />
                  ))}
                  {preview && <AnnotationShape a={preview} />}
                </svg>
              </div>
            </TransformComponent>
          </>
        )}
      </TransformWrapper>
      )}

      {streaming && (
        <div className="absolute top-2 right-2 text-[10px] font-mono px-2 py-1 rounded z-10"
          style={{ background: 'rgba(242,107,31,0.15)', color: '#F26B1F' }}>
          {streamBufLen.toLocaleString()} chars streamed
        </div>
      )}

      {textPrompt && (
        <TextNotePrompt
          x={textPrompt.x} y={textPrompt.y}
          onSave={(text) => {
            if (text?.trim()) {
              setAnnotations([...(annotations || []), { id: textPrompt.id, kind: 'text', x: textPrompt.x, y: textPrompt.y, text }]);
            }
            setTextPrompt(null);
          }}
          onCancel={() => setTextPrompt(null)}
        />
      )}
    </div>
  );
};

const AnnotationShape = ({ a, onRemove }) => {
  if (a.kind === 'rect') {
    return (
      <g className="pointer-events-auto" data-testid={`ann-rect-${a.id}`}>
        <rect x={a.x} y={a.y} width={a.w} height={a.h}
          fill={ANN_FILL} stroke={ANN_STROKE} strokeWidth={3} strokeDasharray="8 4" />
        {onRemove && (
          <foreignObject x={a.x + a.w - 22} y={a.y + 2} width={20} height={20}>
            <button onClick={() => onRemove(a.id)} className="w-5 h-5 rounded-full bg-white shadow flex items-center justify-center text-[10px]"
              style={{ color: ANN_STROKE, border: `1px solid ${ANN_STROKE}` }}>×</button>
          </foreignObject>
        )}
      </g>
    );
  }
  if (a.kind === 'arrow') {
    return (
      <g className="pointer-events-auto" data-testid={`ann-arrow-${a.id}`}>
        <defs>
          <marker id={`arrowhead-${a.id}`} markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill={ANN_STROKE} />
          </marker>
        </defs>
        <line x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2}
          stroke={ANN_STROKE} strokeWidth={3} markerEnd={`url(#arrowhead-${a.id})`} />
      </g>
    );
  }
  if (a.kind === 'text') {
    return (
      <g className="pointer-events-auto" data-testid={`ann-text-${a.id}`}>
        <rect x={a.x - 4} y={a.y - 14} width={Math.max(60, (a.text || '').length * 7)} height={20}
          fill="white" stroke={ANN_STROKE} strokeWidth={1} />
        <text x={a.x} y={a.y} fontSize={12} fill={ANN_STROKE} fontFamily="Inter, sans-serif" fontWeight={600}>
          {a.text}
        </text>
      </g>
    );
  }
  return null;
};

const TextNotePrompt = ({ x, y, onSave, onCancel }) => {
  const [text, setText] = useState('');
  return (
    <div className="absolute z-20 rounded-lg p-2 shadow-lg"
      style={{
        background: 'var(--layer-0)', border: '1px solid var(--hairline)',
        top: '40%', left: '40%',
      }}>
      <input
        autoFocus
        value={text} onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSave(text);
          else if (e.key === 'Escape') onCancel();
        }}
        placeholder={`Note at (${Math.round(x)}, ${Math.round(y)})…`}
        className="px-2 py-1 text-[12px] rounded outline-none w-56"
        style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)', color: 'var(--text-primary)' }}
        data-testid="ds-text-note-input"
      />
      <div className="flex gap-1 mt-1">
        <button onClick={() => onSave(text)} className="text-[10.5px] font-semibold px-2 py-0.5 rounded" style={{ background: ANN_STROKE, color: '#fff' }}>Add</button>
        <button onClick={onCancel} className="text-[10.5px] px-2 py-0.5 rounded hover:bg-nexus-bg-tertiary text-nexus-text-tertiary">Cancel</button>
      </div>
    </div>
  );
};

export default DesignCanvas;
